import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, RotateCcw, Save, Undo2, Redo2, Bold, Italic, Underline, Strikethrough, List, ListOrdered, Link2, AlignLeft, AlignCenter, AlignRight, AlignJustify, Eraser } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { translations } from "../../i18n/translations";
import ExpandableText from "./ExpandableText";

// Pages du site éditables. `extra` regroupe les textes connexes affichés sous le
// même bouton (ex. les témoignages font partie de l'accueil). Toute clé racine de
// translations.js fonctionne sans autre réglage : le site applique les textes
// personnalisés par chemin (lang|chemin).
const SECTIONS = [
  { key: "nav", label: "Barre de navigation" },
  { key: "home", label: "Accueil", extra: ["home_testimonials", "ribbon"] },
  { key: "catalogue", label: "La Boutique", extra: ["category"] },
  { key: "product", label: "Fiche produit", extra: ["productReviews"] },
  { key: "cart", label: "Panier", extra: ["freeship", "shared_cart"] },
  { key: "checkout", label: "Caisse", extra: ["promo", "express"] },
  { key: "catalogues", label: "Catalogues fournisseurs" },
  { key: "forfaits", label: "Forfaits Tournoi", extra: ["forfaitBanner"] },
  { key: "blog", label: "Blogue" },
  { key: "occasions", label: "Occasions" },
  { key: "villes", label: "Pages villes (SEO)" },
  { key: "livraison", label: "Livraison main propre" },
  { key: "realisations", label: "Réalisations" },
  { key: "about", label: "À propos" },
  { key: "faq", label: "FAQ" },
  { key: "contact", label: "Contact" },
  { key: "login", label: "Connexion", extra: ["auth"] },
  { key: "register", label: "Inscription" },
  { key: "pwd", label: "Mot de passe oublié" },
  { key: "verify", label: "Confirmation courriel", extra: ["confirm"] },
  { key: "account", label: "Mon compte", extra: ["account_extra", "mySoumissions"] },
  { key: "referral", label: "Parrainage" },
  { key: "payment", label: "Paiement" },
  { key: "privacy", label: "Confidentialité" },
  { key: "terms", label: "Conditions d'utilisation" },
  { key: "returns", label: "Retours" },
  { key: "maintenance", label: "Maintenance" },
  { key: "footer", label: "Pied de page" },
  { key: "seo", label: "SEO (titres et descriptions)" },
].sort((a, b) => a.label.localeCompare(b.label, "fr"));  // pages en ordre alphabétique

// Liste récursive des chemins de texte éditables d'une section (ex. faq.items.0.q)
function collectPaths(obj, prefix, out = []) {
  for (const [k, v] of Object.entries(obj || {})) {
    const path = `${prefix}.${k}`;
    if (typeof v === "string") out.push(path);
    else if (Array.isArray(v)) v.forEach((item, i) => {
      if (typeof item === "string") out.push(`${path}.${i}`);
      else collectPaths(item, `${path}.${i}`, out);
    });
    else if (v && typeof v === "object") collectPaths(v, path, out);
  }
  return out;
}

export const getAt = (obj, path) => path.split(".").reduce((o, k) => (o != null ? o[k] : undefined), obj);

// Étiquettes conviviales (mode « document Word ») : le chemin technique reste visible
// en petit, mais le champ se présente en langage clair — ex. « Participation — Nom ».
const NICE = {
  kicker: "Sur-titre", title: "Titre", intro: "Introduction", desc: "Description",
  tag: "Slogan", name: "Nom", cta: "Bouton", note: "Note", text: "Texte",
  sub: "Sous-titre", q: "Question", a: "Réponse", h: "Titre", p: "Paragraphe",
  label: "Libellé", meta: "Meta-description (SEO)", popular: "Badge « populaire »",
  total: "Étiquette du total", steps: "Étapes", items: "Éléments",
  item_medal: "Description de la médaille", item_trophy_choice: "Ligne « trophée au choix »",
  item_trophies_choice: "Ligne « trophées au choix »", players_label: "Étiquette du sélecteur",
  players_unit: "Mot « joueurs »", per_player: "Mention « par joueur »",
  volume_note: "Note « prix variables »", cta_sub: "Phrase sous le bouton",
  chosen: "Mention du forfait choisi", account_line: "Ligne « votre compte »",
};

function prettyLabel(path) {
  const parts = path.split(".");
  let last = parts[parts.length - 1];
  let prefix = "";
  if (parts[0] === "forfaits") {                  // p1_/p2_/p3_ → nom du forfait
    const pm = last.match(/^p([123])_(.+)$/);
    if (pm) {
      prefix = `${["", "Participation", "Classique", "Prestige"][Number(pm[1])]} — `;
      last = pm[2];
    }
  }
  const m = last.match(/^(.+?)_(\d+)$/);          // title_1 → « Titre 1 »
  const base = m ? m[1] : last;
  let label = NICE[base] || base.replace(/_/g, " ");
  if (m) label += ` ${m[2]}`;
  if (base.startsWith("form_")) { const rest = base.slice(5); label = `Formulaire — ${NICE[rest] || rest.replace(/_/g, " ")}`; }
  if (base.startsWith("gate_")) { const rest = base.slice(5); label = `Portail d'inscription — ${NICE[rest] || rest.replace(/_/g, " ")}`; }
  const numSeg = parts.find((s) => /^\d+$/.test(s));   // steps.0.h → « Élément 1 — Titre »
  if (numSeg !== undefined) label = `Élément ${Number(numSeg) + 1} — ${label}`;
  label = prefix + label;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// Mini-éditeur de texte enrichi (comme le Word de base) : gras, italique, souligné,
// taille, couleur, effacement. Le HTML propre est sauvegardé comme texte personnalisé
// et s'affiche tel quel sur le site. Sélectionner du texte puis cliquer un bouton.
const RICH_SIZES = [["Petit", "2"], ["Normal", "3"], ["Grand", "5"], ["Très grand", "6"]];

// Polices les plus utilisées : celles du site, les classiques système, les populaires
// Google et les script/signature élégante — chargées via Google Fonts dans index.css.
const RICH_FONTS = [
  ["Cooper Black (titres du site)", '"Cooper Black", serif'],
  ["Manrope (texte du site)", 'Manrope, sans-serif'],
  ["Cormorant Garamond", '"Cormorant Garamond", serif'],
  ["Arial", 'Arial, sans-serif'],
  ["Georgia", 'Georgia, serif'],
  ["Times New Roman", '"Times New Roman", serif'],
  ["Verdana", 'Verdana, sans-serif'],
  ["Courier New", '"Courier New", monospace'],
  ["Impact", 'Impact, sans-serif'],
  ["Playfair Display", '"Playfair Display", serif'],
  ["Montserrat", 'Montserrat, sans-serif'],
  ["Roboto", 'Roboto, sans-serif'],
  ["Lato", 'Lato, sans-serif'],
  ["Open Sans", '"Open Sans", sans-serif'],
  ["Great Vibes — signature", '"Great Vibes", cursive'],
  ["Allura — signature", 'Allura, cursive'],
  ["Dancing Script — script", '"Dancing Script", cursive'],
  ["Parisienne — script", 'Parisienne, cursive'],
  ["Alex Brush — signature", '"Alex Brush", cursive'],
];

export function RichField({ value, onChange, testId }) {
  const ref = useRef(null);
  const savedRange = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || "";
  }, [value]);
  const saveSel = () => {
    const sel = window.getSelection();
    if (sel.rangeCount && ref.current?.contains(sel.anchorNode)) savedRange.current = sel.getRangeAt(0);
  };
  const exec = (cmd, arg = null) => {
    const sel = window.getSelection();
    if (savedRange.current) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
    ref.current?.focus();
    document.execCommand("styleWithCSS", false, true);
    const ok = document.execCommand(cmd, false, arg);
    saveSel();  // sélection rafraîchie : on peut enchaîner couleur + surlignage sans resélectionner
    if (ref.current) onChange(ref.current.innerHTML);
    return ok;
  };
  // Surlignage : hiliteColor (Chrome/Edge) avec repli backColor (Firefox)
  const highlight = (color) => {
    if (!exec("hiliteColor", color)) exec("backColor", color);
  };
  // Lien : sélectionner du texte, cliquer « Lien », entrer l'adresse (vide = retirer le lien)
  const link = () => {
    const url = window.prompt("Adresse du lien — ex. /boutique ou https://… (laisser vide pour retirer le lien)");
    if (url === null) return;
    if (!url.trim()) return exec("unlink");
    const clean = url.trim();
    const href = clean.startsWith("/") || /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
    exec("createLink", href);
  };
  const btn = "px-2.5 py-1 rounded border border-ivory/20 text-ivory/70 hover:border-gold/60 hover:text-gold text-xs transition-colors duration-300";
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 mb-1.5" data-testid={`${testId}-toolbar`}>
        <button type="button" data-testid={`${testId}-undo`} title="Annuler" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("undo")} className={btn}><Undo2 className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`${testId}-redo`} title="Rétablir" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("redo")} className={btn}><Redo2 className="w-3.5 h-3.5" /></button>
        <span className="w-px h-5 bg-ivory/15 mx-0.5" />
        <select data-testid={`${testId}-style`} title="Style : paragraphe ou titre" defaultValue=""
          onMouseDown={saveSel}
          onChange={(e) => { if (e.target.value) exec("formatBlock", e.target.value); e.target.value = ""; }}
          className={`${btn} bg-ink`}>
          <option value="" disabled>Style</option>
          <option value="p">Paragraphe</option>
          <option value="h3">Titre</option>
          <option value="h4">Sous-titre</option>
        </select>
        <select data-testid={`${testId}-size`} title="Taille du texte" defaultValue=""
          onMouseDown={saveSel}
          onChange={(e) => { if (e.target.value) exec("fontSize", e.target.value); e.target.value = ""; }}
          className={`${btn} bg-ink`}>
          <option value="" disabled>Taille</option>
          {RICH_SIZES.map(([lab, v]) => <option key={v} value={v}>{lab}</option>)}
        </select>
        <select data-testid={`${testId}-font`} title="Police du texte" defaultValue=""
          onMouseDown={saveSel}
          onChange={(e) => { if (e.target.value) exec("fontName", e.target.value); e.target.value = ""; }}
          className={`${btn} bg-ink max-w-36`}>
          <option value="" disabled>Police</option>
          {RICH_FONTS.map(([lab, fam]) => <option key={fam} value={fam} style={{ fontFamily: fam }}>{lab}</option>)}
        </select>
        <span className="w-px h-5 bg-ivory/15 mx-0.5" />
        <button type="button" data-testid={`${testId}-bold`} title="Gras" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")} className={btn}><Bold className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`${testId}-italic`} title="Italique" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")} className={btn}><Italic className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`${testId}-underline`} title="Souligné" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("underline")} className={btn}><Underline className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`${testId}-strike`} title="Barré" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("strikeThrough")} className={btn}><Strikethrough className="w-3.5 h-3.5" /></button>
        <input type="color" defaultValue="#D4AF37" title="Couleur du texte" data-testid={`${testId}-color`}
          onMouseDown={saveSel} onChange={(e) => exec("foreColor", e.target.value)}
          className="w-8 h-7 rounded border border-ivory/20 bg-ink cursor-pointer" />
        <input type="color" defaultValue="#f5d90a" title="Surlignage (couleur de fond du texte)" data-testid={`${testId}-highlight`}
          onMouseDown={saveSel} onChange={(e) => highlight(e.target.value)}
          className="w-8 h-7 rounded border border-ivory/20 bg-ink cursor-pointer" />
        <span className="w-px h-5 bg-ivory/15 mx-0.5" />
        <button type="button" data-testid={`${testId}-ul`} title="Liste à puces" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")} className={btn}><List className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`${testId}-ol`} title="Liste numérotée" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertOrderedList")} className={btn}><ListOrdered className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`${testId}-link`} title="Lien — rendre le texte sélectionné cliquable" onMouseDown={(e) => e.preventDefault()} onClick={link} className={btn}><Link2 className="w-3.5 h-3.5" /></button>
        <span className="w-px h-5 bg-ivory/15 mx-0.5" />
        <button type="button" data-testid={`${testId}-align-left`} title="Aligner à gauche" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyLeft")} className={btn}><AlignLeft className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`${testId}-align-center`} title="Centrer" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyCenter")} className={btn}><AlignCenter className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`${testId}-align-right`} title="Aligner à droite" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyRight")} className={btn}><AlignRight className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`${testId}-align-justify`} title="Justifier" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("justifyFull")} className={btn}><AlignJustify className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid={`${testId}-clear`} title="Effacer toute la mise en forme" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("removeFormat")} className={btn}><Eraser className="w-3.5 h-3.5" /></button>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning
        data-testid={testId}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        onKeyUp={saveSel} onMouseUp={saveSel}
        className="rich-edit w-full min-h-[70px] bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-sm leading-relaxed focus:border-gold focus:outline-none" />
    </div>
  );
}

export default function ContentEditor({ fixedSection } = {}) {
  const [section, setSection] = useState(fixedSection || "home");
  const [overrides, setOverrides] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [prices, setPrices] = useState(null);   // prix des forfaits (réglages admin)
  const [mins, setMins] = useState(null);       // minimum de joueurs par forfait (réglages admin)

  useEffect(() => {
    api.get("/settings/content")
      .then((r) => setOverrides(r.data?.overrides || {}))
      .catch(() => setOverrides({}));
    // Prix + minimums des forfaits modifiables (forfait_price_* / forfait_min_*)
    // — affichés dans la section Forfaits
    api.get("/admin/settings")
      .then((r) => {
        setPrices({
          participation: r.data.forfait_price_participation ?? "",
          classique: r.data.forfait_price_classique ?? "",
          prestige: r.data.forfait_price_prestige ?? "",
        });
        setMins({
          participation: r.data.forfait_min_participation ?? "",
          classique: r.data.forfait_min_classique ?? "",
          prestige: r.data.forfait_min_prestige ?? "",
        });
      })
      .catch(() => { setPrices(null); setMins(null); });
  }, []);

  // Enregistrer un prix de forfait : vide (null) = retour au prix officiel (10,95/14,95/19,95 $)
  const savePrice = async (pkg) => {
    const raw = String(prices[pkg] ?? "").trim().replace(",", ".");
    const value = raw === "" ? null : parseFloat(raw);
    if (value !== null && (!Number.isFinite(value) || value <= 0)) {
      toast.error("Prix invalide — ex. 10,95");
      return;
    }
    setBusy(`price-${pkg}`);
    try {
      await api.patch("/admin/settings", { [`forfait_price_${pkg}`]: value });
      toast.success(value === null
        ? "Prix officiel rétabli (10,95 $ / 14,95 $ / 19,95 $)."
        : `Prix du forfait enregistré (${value.toFixed(2)} $) — visible immédiatement sur la page Forfaits Tournoi.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  // Enregistrer le minimum de joueurs : vide (null) = minimum officiel (12/24/24)
  const saveMin = async (pkg) => {
    const raw = String(mins[pkg] ?? "").trim();
    const value = raw === "" ? null : parseInt(raw, 10);
    if (value !== null && (!Number.isFinite(value) || value < 1 || value > 500)) {
      toast.error("Minimum invalide — ex. 12 (1 à 500)");
      return;
    }
    setBusy(`min-${pkg}`);
    try {
      await api.patch("/admin/settings", { [`forfait_min_${pkg}`]: value });
      toast.success(value === null
        ? "Minimum officiel rétabli (Bronze 12 / Argent 24 / Or 24 joueurs)."
        : `Minimum du forfait enregistré (${value} joueurs) — visible immédiatement sur la page Forfaits Tournoi.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const paths = useMemo(() => {
    const sec = SECTIONS.find((s) => s.key === section) || SECTIONS[0];
    return [sec.key, ...(sec.extra || [])]
      .flatMap((k) => collectPaths(translations.fr[k], k));
  }, [section]);

  const valueOf = (path, lang) =>
    drafts[path]?.[lang] ?? overrides?.[`${lang}|${path}`] ?? getAt(translations[lang], path) ?? "";
  const isCustom = (path) =>
    overrides && (overrides[`fr|${path}`] !== undefined || overrides[`en|${path}`] !== undefined);
  const setDraft = (path, lang, v) =>
    setDrafts((d) => ({ ...d, [path]: { ...(d[path] || {}), [lang]: v } }));

  const refresh = async () => {
    const { data } = await api.get("/settings/content");
    setOverrides(data.overrides || {});
  };

  const save = async (path) => {
    setBusy(path);
    try {
      for (const lang of ["fr", "en"]) {
        const v = valueOf(path, lang);
        const def = getAt(translations[lang], path) ?? "";
        const cur = overrides[`${lang}|${path}`];
        if (v === def) {
          if (cur !== undefined) await api.delete("/admin/content", { params: { lang, path } });
        } else if (v !== cur) {
          await api.put("/admin/content", { lang, path, value: v });
        }
      }
      await refresh();
      setDrafts((d) => { const n = { ...d }; delete n[path]; return n; });
      toast.success("Texte enregistré — visible sur le site.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const reset = async (path) => {
    setBusy(path);
    try {
      for (const lang of ["fr", "en"]) await api.delete("/admin/content", { params: { lang, path } });
      await refresh();
      setDrafts((d) => { const n = { ...d }; delete n[path]; return n; });
      toast.success("Texte revenu à la version d'origine.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid={fixedSection ? `content-editor-${fixedSection}` : "content-editor"}>
      {!fixedSection && (
      <p className="text-sm text-ivory/60 mb-6 max-w-3xl">
        Modifiez n'importe quel texte du site, en français et en anglais. Choisissez la page,
        ajustez le texte puis « Enregistrer » — le changement est en ligne immédiatement.
        « Réinitialiser » ramène le texte d'origine. La barre d'outils (gras, italique, souligné,
        taille, couleur) met en forme le texte sélectionné. Évitez la mise en forme dans les
        numéros de téléphone, courriels et champs SEO (titres et descriptions).
      </p>
      )}
      {!fixedSection && (
      <div className="flex flex-wrap gap-2 mb-8" data-testid="content-sections">
        {SECTIONS.map((s) => (
          <button key={s.key} data-testid={`content-section-${s.key}`} onClick={() => setSection(s.key)}
            className={`px-4 py-2 rounded-full text-xs border transition-colors duration-300 ${
              section === s.key ? "bg-gold text-ink border-gold font-semibold" : "border-ivory/20 text-ivory/70 hover:border-gold/60 hover:text-gold"
            }`}>
            {s.label}
          </button>
        ))}
      </div>
      )}

      {/* Prix + minimums des forfaits modifiables — en haut de la section Forfaits Tournoi
          (les gros prix de la page publique ; vide = officiel) */}
      {section === "forfaits" && !fixedSection && prices && mins && (
        <div className="bg-surface border border-gold/40 rounded-xl p-5 mb-6" data-testid="content-forfait-prices">
          <p className="text-xs uppercase tracking-widest text-gold mb-1">Prix des forfaits (par joueur) et minimum de joueurs</p>
          <p className="text-xs text-ivory/55 mb-4">
            Ce sont les gros prix et minimums affichés sur la page Forfaits Tournoi. Videz le champ puis
            « Enregistrer » pour revenir à l'officiel. Bronze/Argent = médaille par joueur (Argent ajoute
            1 trophée d'équipe au prix du modèle choisi) · Or = 1 trophée par joueur, prix fixe.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[["participation", "Bronze", "10,95", "12"], ["classique", "Argent", "14,95", "24"], ["prestige", "Or", "19,95", "24"]].map(([id, label, official, officialMin]) => (
              <div key={id} className="bg-ink border border-ivory/10 rounded-lg p-3.5 space-y-3">
                <div>
                  <label htmlFor={`content-forfait-price-${id}`} className="block text-xs text-ivory/65 mb-1.5">
                    {label} — <span className="text-ivory/40">officiel : {official} $</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input id={`content-forfait-price-${id}`} value={prices[id]} placeholder={official}
                      data-testid={`content-forfait-price-${id}`} type="text" inputMode="decimal"
                      onChange={(e) => setPrices({ ...prices, [id]: e.target.value })}
                      className="w-24 bg-surface border border-ivory/15 rounded px-2.5 py-1.5 text-sm text-right focus:border-gold focus:outline-none" />
                    <button type="button" onClick={() => savePrice(id)} disabled={busy === `price-${id}`}
                      data-testid={`content-forfait-price-save-${id}`}
                      className="inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-semibold px-3.5 py-1.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
                      {busy === `price-${id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Enregistrer
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor={`content-forfait-min-${id}`} className="block text-xs text-ivory/65 mb-1.5">
                    Minimum de joueurs — <span className="text-ivory/40">officiel : {officialMin}</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input id={`content-forfait-min-${id}`} value={mins[id]} placeholder={officialMin}
                      data-testid={`content-forfait-min-${id}`} type="text" inputMode="numeric"
                      onChange={(e) => setMins({ ...mins, [id]: e.target.value })}
                      className="w-24 bg-surface border border-ivory/15 rounded px-2.5 py-1.5 text-sm text-right focus:border-gold focus:outline-none" />
                    <button type="button" onClick={() => saveMin(id)} disabled={busy === `min-${id}`}
                      data-testid={`content-forfait-min-save-${id}`}
                      className="inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-semibold px-3.5 py-1.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
                      {busy === `min-${id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Enregistrer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {overrides === null ? (
        <Loader2 className="w-6 h-6 animate-spin text-gold" />
      ) : (
        <div className="space-y-4">
          {paths.map((path) => {
            const dirty = !!drafts[path];
            return (
              <div key={path} data-testid={`content-field-${path}`}
                className={`bg-surface border rounded-xl p-5 transition-colors duration-300 ${isCustom(path) ? "border-gold/40" : "border-ivory/10"}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ivory/90" data-testid={`content-label-${path}`}>{prettyLabel(path)}</p>
                    <ExpandableText text={path} testId={`content-path-${path}`} className="min-w-0 text-[10px] text-ivory/40 font-mono" />
                  </div>
                  {isCustom(path) && (
                    <span className="text-[10px] uppercase tracking-widest text-gold border border-gold/40 rounded-full px-2.5 py-1 shrink-0" data-testid={`content-custom-${path}`}>
                      personnalisé
                    </span>
                  )}
                </div>
                {/* Mode « document Word » : le français puis l'anglais, l'un sous l'autre.
                    NB : des <div>, pas des <label> — un label activerait le 1er bouton de la
                    barre d'outils (« Annuler ») à chaque clic dans le champ, effaçant la frappe. */}
                <div className="space-y-3">
                  <div className="block">
                    <span className="block text-[10px] uppercase tracking-widest text-ivory/65 mb-1">Français</span>
                    <RichField testId={`content-fr-${path}`}
                      value={valueOf(path, "fr")} onChange={(v) => setDraft(path, "fr", v)} />
                  </div>
                  <div className="block">
                    <span className="block text-[10px] uppercase tracking-widest text-ivory/65 mb-1">English</span>
                    <RichField testId={`content-en-${path}`}
                      value={valueOf(path, "en")} onChange={(v) => setDraft(path, "en", v)} />
                  </div>
                </div>
                <div className="flex gap-3 mt-3">
                  {dirty && (
                    <button data-testid={`content-save-${path}`} onClick={() => save(path)} disabled={busy === path}
                      className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-2 rounded-full text-xs hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                      {busy === path ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Enregistrer
                    </button>
                  )}
                  {isCustom(path) && (
                    <button data-testid={`content-reset-${path}`} onClick={() => reset(path)} disabled={busy === path}
                      className="inline-flex items-center gap-2 border border-ivory/20 text-ivory/60 px-5 py-2 rounded-full text-xs hover:border-gold/50 hover:text-gold transition-colors duration-300 disabled:opacity-60">
                      <RotateCcw className="w-3.5 h-3.5" /> Réinitialiser
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
